/* eslint-disable react-hooks/set-state-in-effect */
/**
 * SwitchRoleModal — Admin role assumption dialog
 *
 * Displays available tenant roles and lets the admin assume one
 * to preview the application from that role's perspective.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Shield, ShieldCheck, Search, X, Loader2 } from 'lucide-react'
import { useAssumedRole, type SwitchableRole } from '../contexts/AssumedRoleContext'
import './SwitchRoleModal.css'

interface SwitchRoleModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * platform_admin can only be granted on top of the tenant `admin` role, so a
 * platform admin always holds both and the backend flags both as their own.
 */
const isPlatformAdminRole = (name: string) =>
  name.toLowerCase().replace(/[\s-]/g, '_') === 'platform_admin'

export function SwitchRoleModal({ isOpen, onClose }: SwitchRoleModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { assumeRole, clearAssumedRole, assumedRole, isAssuming, fetchSwitchableRoles } =
    useAssumedRole()
  const [roles, setRoles] = useState<SwitchableRole[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  /** Portaled so it is not clipped by the scrollable role list (overflow-y: auto). */
  const [descTooltip, setDescTooltip] = useState<{
    text: string
    left: number
    maxWidth: number
    maxHeight: number
    top?: number
    bottom?: number
  } | null>(null)
  const hideTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadRoles = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await fetchSwitchableRoles()
      setRoles(data)
    } catch {
      setError('Failed to load roles. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [fetchSwitchableRoles])

  useEffect(() => {
    if (isOpen) {
      loadRoles()
      setSearchQuery('')
    }
  }, [isOpen, loadRoles])

  useEffect(() => {
    if (!isOpen) {
      setDescTooltip(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (!descTooltip) return
    const clear = () => setDescTooltip(null)
    window.addEventListener('scroll', clear, true)
    window.addEventListener('resize', clear)
    return () => {
      window.removeEventListener('scroll', clear, true)
      window.removeEventListener('resize', clear)
    }
  }, [descTooltip])

  const clearHideTooltipTimeout = () => {
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current)
      hideTooltipTimeoutRef.current = null
    }
  }

  const showRoleDescriptionTooltip = (anchor: HTMLElement, text: string) => {
    clearHideTooltipTimeout()
    const r = anchor.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const maxWidth = Math.min(360, vw - 16)
    let left = r.left
    if (left + maxWidth > vw - 8) left = Math.max(8, vw - 8 - maxWidth)
    const margin = 8
    const spaceBelow = vh - r.bottom - margin
    const spaceAbove = r.top - margin
    const preferBelow = spaceBelow >= 72 || spaceBelow >= spaceAbove
    if (preferBelow) {
      const maxHeight = Math.min(280, Math.max(80, spaceBelow))
      setDescTooltip({
        text,
        left,
        maxWidth,
        maxHeight,
        top: r.bottom + 6,
      })
    } else {
      const maxHeight = Math.min(280, Math.max(80, spaceAbove))
      setDescTooltip({
        text,
        left,
        maxWidth,
        maxHeight,
        bottom: vh - r.top + 6,
      })
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    return () => {
      if (hideTooltipTimeoutRef.current) clearTimeout(hideTooltipTimeoutRef.current)
    }
  }, [])

  const scheduleHideRoleDescriptionTooltip = () => {
    clearHideTooltipTimeout()
    hideTooltipTimeoutRef.current = setTimeout(() => setDescTooltip(null), 120)
  }

  const handleSelectRole = async (role: SwitchableRole) => {
    setDescTooltip(null)
    clearHideTooltipTimeout()

    // platform_admin is a privilege, not an assumable permission set: assuming it
    // would strip the platform-admin bypass and evaluate the (empty) role
    // permissions, breaking the UI. The backend only lists this entry to real
    // platform admins, so selecting it always means "revert to my real identity".
    if (assumedRole?.id === role.id || isPlatformAdminRole(role.name)) {
      clearAssumedRole()
      onClose()
      return
    }
    try {
      await assumeRole(role.id)
      onClose()
      // Land on root, not the current path: the assumed role may not have access
      // to the page the admin was on (e.g. /platform-admin). HomeRoute then routes
      // to the first sidebar entry this role can actually see, matching a real login.
      navigate('/')
    } catch {
      setError('Failed to switch role. Please try again.')
    }
  }

  const handleClearRole = () => {
    clearAssumedRole()
    onClose()
  }

  if (!isOpen) return null

  // Derived from the full list, not the filtered one, so searching cannot move the badge.
  const ownsPlatformAdmin = roles.some((r) => r.is_own_role && isPlatformAdminRole(r.name))

  const filteredRoles = roles.filter(
    (r) =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.description && r.description.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  return (
    <div className="switch-role-overlay" onClick={onClose}>
      <div className="switch-role-modal" onClick={(e) => e.stopPropagation()}>
        <div className="switch-role-modal__header">
          <div className="switch-role-modal__title">
            <Shield size={18} />
            <span>{t('nav.switchRole')}</span>
          </div>
          <button className="switch-role-modal__close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <p className="switch-role-modal__description">
          Preview the application as a different role. Your admin access is preserved — this only
          changes how permissions are evaluated.
        </p>

        {assumedRole && (
          <div className="switch-role-modal__current-assumption">
            <ShieldCheck size={14} />
            <span>
              Currently viewing as <strong>{assumedRole.name}</strong>
            </span>
            <button className="switch-role-modal__clear-btn" onClick={handleClearRole}>
              Revert to my role
            </button>
          </div>
        )}

        <div className="switch-role-modal__search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="switch-role-modal__list">
          {isLoading && (
            <div className="switch-role-modal__loading">
              <Loader2 size={20} className="animate-spin" />
              <span>Loading roles...</span>
            </div>
          )}

          {error && <div className="switch-role-modal__error">{error}</div>}

          {!isLoading &&
            !error &&
            filteredRoles.map((role) => {
              const isActive = assumedRole?.id === role.id
              // "Your role" names one identity: for a platform admin that is
              // platform_admin, never the tenant `admin` role they also carry.
              const isOwnRole =
                role.is_own_role && (!ownsPlatformAdmin || isPlatformAdminRole(role.name))
              const desc = role.description?.trim()
              return (
                <button
                  key={role.id}
                  type="button"
                  className={`switch-role-modal__role${isActive ? ' switch-role-modal__role--active' : ''}${isOwnRole ? ' switch-role-modal__role--own' : ''}`}
                  onClick={() => handleSelectRole(role)}
                  disabled={isAssuming}
                  onMouseEnter={(e) => {
                    if (desc) showRoleDescriptionTooltip(e.currentTarget, desc)
                  }}
                  onMouseLeave={scheduleHideRoleDescriptionTooltip}
                >
                  <div className="switch-role-modal__role-info">
                    <span className="switch-role-modal__role-name">
                      {role.name}
                      {isOwnRole && (
                        <span className="switch-role-modal__role-badge">Your role</span>
                      )}
                    </span>
                    {role.description && (
                      <span className="switch-role-modal__role-desc">{role.description}</span>
                    )}
                  </div>
                  {isActive && <ShieldCheck size={16} className="switch-role-modal__role-check" />}
                </button>
              )
            })}

          {!isLoading && !error && filteredRoles.length === 0 && (
            <div className="switch-role-modal__empty">No roles match your search.</div>
          )}
        </div>
      </div>
      {descTooltip &&
        createPortal(
          <div
            role="tooltip"
            className="switch-role-modal__desc-tooltip"
            style={{
              position: 'fixed',
              left: descTooltip.left,
              zIndex: 100001,
              maxWidth: descTooltip.maxWidth,
              maxHeight: descTooltip.maxHeight,
              overflowY: 'auto',
              ...(descTooltip.top !== undefined ? { top: descTooltip.top } : {}),
              ...(descTooltip.bottom !== undefined ? { bottom: descTooltip.bottom } : {}),
            }}
          >
            {descTooltip.text}
          </div>,
          document.body
        )}
    </div>
  )
}
